import React from 'react';
import { Icon, ICONS } from '@/components/brand';

type NotificationType = 'success' | 'error' | 'warning' | 'info';

interface NotificationModalProps {
  isOpen: boolean;
  type: NotificationType;
  title: string;
  message: string;
  onClose: () => void;
}

const META: Record<NotificationType, { d: string; color: string; bg: string; border: string; pillClass: string; label: string }> = {
  success: {
    d: ICONS.check,
    color: 'var(--success)',
    bg: 'rgba(130,212,155,0.10)',
    border: 'rgba(130,212,155,0.30)',
    pillClass: 'sh-pill-success',
    label: 'Success',
  },
  error: {
    d: ICONS.close,
    color: 'var(--danger)',
    bg: 'rgba(240,131,131,0.10)',
    border: 'rgba(240,131,131,0.30)',
    pillClass: 'sh-pill-danger',
    label: 'Error',
  },
  warning: {
    d: ICONS.warn,
    color: 'var(--accent)',
    bg: 'var(--accent-soft)',
    border: 'var(--accent-dim)',
    pillClass: 'sh-pill-warn',
    label: 'Warning',
  },
  info: {
    d: ICONS.info,
    color: 'var(--info)',
    bg: 'var(--info-soft)',
    border: 'rgba(108,171,245,0.25)',
    pillClass: 'sh-pill-info',
    label: 'Info',
  },
};

export const NotificationModal: React.FC<NotificationModalProps> = ({ isOpen, type, title, message, onClose }) => {
  if (!isOpen) return null;
  const meta = META[type];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(8, 8, 9, 0.78)', backdropFilter: 'blur(6px)' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="notification-dialog-title"
      onClick={onClose}
    >
      <div
        className="sh-card"
        style={{ maxWidth: 420, width: '100%', margin: '0 16px', padding: '28px 28px 24px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between" style={{ marginBottom: 18 }}>
          <span className={`sh-pill ${meta.pillClass}`}>{meta.label}</span>
          <button
            type="button"
            onClick={onClose}
            className="sh-btn sh-btn-ghost sh-btn-sm"
            aria-label="Close"
            style={{ padding: 6 }}
          >
            <Icon d={ICONS.close} size={14} />
          </button>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 48,
            height: 48,
            borderRadius: 'var(--r-md)',
            background: meta.bg,
            border: `1px solid ${meta.border}`,
            color: meta.color,
            marginBottom: 18,
          }}
        >
          <Icon d={meta.d} size={20} stroke={1.6} />
        </div>

        <h2 id="notification-dialog-title" className="sh-h2" style={{ marginBottom: 8 }}>
          {title}
        </h2>
        <p style={{ color: 'var(--text-2)', fontSize: 13, whiteSpace: 'pre-line', lineHeight: 1.55 }}>{message}</p>

        <button type="button" onClick={onClose} className="sh-btn sh-btn-primary sh-btn-block" style={{ marginTop: 24 }}>
          Close
        </button>
      </div>
    </div>
  );
};
